import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { MutationSchema, MutationTypename } from "shared/api/types"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ClarificationRequest } from "shared/types/__generated__/graphql"
import {
  ClarificationRequestSchema,
  UpdateClarificationRequestInputSchema
} from "shared/types/__generated__/validation"

import {
  ClarificationRequestFragment,
  ClarificationRequestFragmentDoc
} from "entities/clarification-requests/graphql/fragments.generated"
import {
  UpdateClarificationRequestDocument,
  UpdateClarificationRequestMutation,
  UpdateClarificationRequestMutationVariables
} from "entities/clarification-requests/graphql/mutations.generated"
import { GetClarificationRequestDocument } from "entities/clarification-requests/graphql/queries.generated"
import z from "zod"

import {
  ClarificationRequestResourceType,
  ClarificationRequestTypename
} from "../types/ClarificationRequest"

const UpdateClarificationRequestVariablesSchema = z.object({
  resourceType: z.string(),
  id: z.string(),
  input: UpdateClarificationRequestInputSchema()
})

export function useUpdateClarificationRequest() {
  const { t } = useTranslation()
  const { cache } = useApolloClient()
  const { userInfo } = useAuth()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t(
        "Request.ClarificationRequest.UPDATE_CLARIFICATION_REQUEST.Start"
      ),
      success: t(
        "Request.ClarificationRequest.UPDATE_CLARIFICATION_REQUEST.Success"
      ),
      failure: t(
        "Request.ClarificationRequest.UPDATE_CLARIFICATION_REQUEST.Failure"
      )
    }
  })

  const [apolloMutate, rest] = useApolloMutation<
    UpdateClarificationRequestMutation,
    UpdateClarificationRequestMutationVariables
  >(UpdateClarificationRequestDocument)

  const updateClarificationRequest = useEvent(
    ({
      resourceType,
      id,
      content
    }: {
      resourceType: ClarificationRequestResourceType
      id: string
      content: string
    }) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()

      const existing = cache.readFragment<ClarificationRequestFragment>({
        id: cache.identify({
          __typename: ClarificationRequestTypename,
          id: `${resourceType}:${id}`
        }),
        fragment: ClarificationRequestFragmentDoc,
        fragmentName: "ClarificationRequest"
      })

      return callMutation(apolloMutate, {
        variables: {
          resourceType,
          id,
          input: { id: `${resourceType}:${id}`, content }
        },
        transform: data => data.clarificationRequest as ClarificationRequest,
        schemas: {
          input: UpdateClarificationRequestVariablesSchema,
          response: MutationSchema.extend({
            clarificationRequest: ClarificationRequestSchema()
          })
        },
        apolloOptions: {
          optimisticResponse: existing
            ? {
                __typename: MutationTypename,
                clarificationRequest: {
                  ...existing,
                  content,
                  updatedAt: new Date().toISOString(),
                  lastUpdatedBy:
                    userInfo?.id && userInfo?.email
                      ? {
                          __typename: "User" as const,
                          id: userInfo.id,
                          name: userInfo.name ?? null,
                          email: userInfo.email,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString()
                        }
                      : existing.lastUpdatedBy
                }
              }
            : undefined,
          refetchQueries: result => {
            if (result.errors) {
              return [
                {
                  query: GetClarificationRequestDocument,
                  variables: { id, resourceType }
                }
              ]
            }
            return []
          },
          onCompleted: () => {
            raiseSuccessToast()
          },
          onError: () => {
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [updateClarificationRequest, rest] as [
    typeof updateClarificationRequest,
    typeof rest
  ]
}
